import { useEffect, useState } from 'react'
import type { ConnectionStatus, MessageSource } from '../lib/messageSource'
import type { Cart, DisplayMessage, Place } from '../types/display'

/** Nothing from the till for this long and the screen goes back to welcome,
 *  so last night's order or a stale "payment failed" never greets a customer. */
export const IDLE_AFTER_MS = 15 * 60 * 1000

/**
 * Subscribes to `source` and keeps the latest message, the last known cart
 * (which persists underneath non-cart moments), whether the link to the
 * till is up, and the shop's branding.
 *
 * `source` must be referentially stable across renders — memoise it with
 * `useMemo` (or hoist it) at the call site. The subscription is torn down
 * and re-established whenever `source`'s identity changes.
 */
export function useDisplayMessage(
  source: MessageSource,
  idleAfterMs: number = IDLE_AFTER_MS,
): {
  message: DisplayMessage | null
  cart: Cart | null
  status: ConnectionStatus
  place: Place | null
} {
  const [message, setMessage] = useState<DisplayMessage | null>(null)
  const [cart, setCart] = useState<Cart | null>(null)
  const [status, setStatus] = useState<ConnectionStatus>('online')
  const [place, setPlace] = useState<Place | null>(null)

  useEffect(() => {
    let idle: ReturnType<typeof setTimeout> | undefined
    const armIdle = (ms: number) => {
      clearTimeout(idle)
      idle = setTimeout(() => {
        setMessage(null)
        setCart(null)
      }, ms)
    }

    const unsubscribe = source.subscribe({
      message(next, stored) {
        // A stored message was left waiting, maybe by a till that died
        // mid-sale. Its age counts against the window, and an expired one is
        // not shown. Live pushes are trusted, so the two clocks never matter.
        let ms = idleAfterMs
        if (stored && next.created) ms = Math.min(ms, ms - (Date.now() - next.created * 1000))
        if (ms <= 0) return
        setMessage(next)
        if (next.type === 'cart_update') setCart(next.cart)
        armIdle(ms)
      },
      status: setStatus,
      // The cart under a finished moment keeps its idle window running.
      clear(scope) {
        setMessage(null)
        if (scope === 'moment') return
        clearTimeout(idle)
        setCart(null)
      },
      branding: setPlace,
    })
    return () => {
      clearTimeout(idle)
      unsubscribe()
    }
  }, [source, idleAfterMs])

  return { message, cart, status, place }
}
